import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Mail, Globe, FileText, ArrowRight } from 'lucide-react';

// Logo Exevori
const LOGO = "https://customer-assets.emergentagent.com/job_connected-work/artifacts/kalrhzgp_remove.photos-removed-background%20Rxevori.png";

export default function ContactProPage() {
  const canvasRef = useRef(null);

  // Animation circuit PCB en arrière-plan
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const nodes = [];
    const nodeCount = 60;
    
    for (let i = 0; i < nodeCount; i++) {
      nodes.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 2 + 1
      });
    }

    const animate = () => {
      ctx.fillStyle = 'rgba(10, 26, 26, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      nodes.forEach(node => {
        node.x += node.vx;
        node.y += node.vy;
        
        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;
        
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(79, 142, 247, 0.6)';
        ctx.fill();
      });

      // Draw connections
      nodes.forEach((node, i) => {
        nodes.slice(i + 1).forEach(other => {
          const dx = node.x - other.x;
          const dy = node.y - other.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < 150) {
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(other.x, other.y);
            ctx.strokeStyle = `rgba(79, 142, 247, ${0.2 * (1 - dist / 150)})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        });
      });

      requestAnimationFrame(animate);
    };
    
    animate();

    return () => window.removeEventListener('resize', resize);
  }, []);

  return (
    <main className="min-h-screen relative overflow-hidden" style={{ backgroundColor: '#0A1A1A' }}>
      {/* Canvas animation */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
      
      {/* Glow effects */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-20 blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, #4F8EF7, transparent)' }} />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-15 blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, #9B59F5, transparent)' }} />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-12">
        {/* Badge */}
        <div className="flex items-center gap-2 mb-6">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-gray-400 text-sm font-inter tracking-wider">Agence numérique</span>
        </div>

        {/* Logo & Brand */}
        <div className="text-center mb-8">
          <img src={LOGO} alt="Exevori" className="h-16 mx-auto mb-4" />
          <h1 className="font-sora font-black text-3xl text-white tracking-tight">EXEVORI</h1>
          <p className="text-gray-400 font-inter mt-2">Contactez-nous</p>
        </div>

        {/* Divider */}
        <div className="w-16 h-0.5 gradient-bg rounded-full mb-8" />

        {/* Contact Card */}
        <div className="w-full max-w-md">
          <div className="backdrop-blur-xl rounded-2xl p-8 border border-white/10"
            style={{ background: 'rgba(21, 46, 46, 0.8)' }}>
            
            {/* Phone */}
            <a href="tel:+15817004171" className="flex items-center gap-4 p-4 rounded-xl hover:bg-white/5 transition-colors group">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, rgba(79,142,247,0.2), rgba(155,89,245,0.2))' }}>
                <Phone size={20} className="text-exevori-primary" />
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Téléphone</div>
                <div className="text-white font-semibold group-hover:text-exevori-primary transition-colors">
                  (581) 700-4171
                </div>
              </div>
            </a>

            <div className="h-px bg-white/10 mx-4" />

            {/* Address */}
            <a href="https://maps.google.com/?q=114+rue+des+Pruniers+Levis+QC" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 rounded-xl hover:bg-white/5 transition-colors group">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, rgba(79,142,247,0.2), rgba(155,89,245,0.2))' }}>
                <MapPin size={20} className="text-exevori-primary" />
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Adresse</div>
                <div className="text-white font-semibold group-hover:text-exevori-primary transition-colors">
                  114 rue des Pruniers<br />
                  <span className="text-gray-400 font-normal text-sm">Lévis, QC G6V 8W2</span>
                </div>
              </div>
            </a>

            <div className="h-px bg-white/10 mx-4" />

            {/* Email */}
            <a href="mailto:contact@exevori.com" className="flex items-center gap-4 p-4 rounded-xl hover:bg-white/5 transition-colors group">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, rgba(79,142,247,0.2), rgba(155,89,245,0.2))' }}>
                <Mail size={20} className="text-exevori-primary" />
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Courriel</div>
                <div className="text-white font-semibold group-hover:text-exevori-primary transition-colors">
                  contact@exevori.com
                </div>
              </div>
            </a>

            <div className="h-px bg-white/10 mx-4" />

            {/* Website */}
            <a href="https://www.exevori.com/" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 rounded-xl hover:bg-white/5 transition-colors group">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, rgba(79,142,247,0.2), rgba(155,89,245,0.2))' }}>
                <Globe size={20} className="text-exevori-primary" />
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Site web</div>
                <div className="text-white font-semibold group-hover:text-exevori-primary transition-colors">
                  www.exevori.com
                </div>
              </div>
            </a>

            {/* CTA Button */}
            <div className="mt-6 px-4">
              <Link to="/devis" className="btn-primary w-full justify-center text-center">
                <FileText size={18} />
                Demander un devis
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
